import { Edit } from 'lucide-react'
import { useState, useRef, useContext } from 'react'
import { toast } from 'sonner'

import axiosInstance from '@/api/axiosInstance'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { AuthContext } from '@/contexts/auth-context'
import { Textarea } from '../../components/ui/textarea'
import logger from '../../utils/logger'

const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5MB
const ALLOWED_FILE_TYPES = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif']

function UpdateUserProfile() {
  const { auth, setAuth } = useContext(AuthContext)
  const { user } = auth
  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    profile_image: user?.profile_image || '',
    address: user?.address || '',
    city: user?.city || '',
    state: user?.state || '',
    postal_code: user?.postal_code || '',
    country: user?.country || '',
    gender: user?.gender || '',
    dob: user?.dob ? new Date(user.dob).toISOString().split('T')[0] : '', // YYYY-mm-dd
  })
  const [loading, setLoading] = useState(false)
  const [selectedImage, setSelectedImage] = useState(null)
  const [imageError, setImageError] = useState('')
  const [isEditing, setIsEditing] = useState(false)

  const fileInputRef = useRef(null)

  const validateImage = (file) => {
    if (!file) {
      setImageError('')
      return true
    }

    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      setImageError('Only JPG, JPEG, PNG, GIF images are allowed')
      return false
    }

    if (file.size > MAX_FILE_SIZE) {
      setImageError('Image size must be less than 5MB')
      return false
    }

    setImageError('')
    return true
  }

  const handleImageChange = (e) => {
    const file = e.target.files[0]
    if (!file) {
      setSelectedImage(null)
      return
    }

    if (!validateImage(file)) {
      e.target.value = ''
      return
    }

    setSelectedImage(file)
    const reader = new FileReader()
    reader.onloadend = () => {
      setProfileData((prev) => ({
        ...prev,
        profile_image: reader.result,
      }))
    }
    reader.readAsDataURL(file)
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setProfileData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleSave = async () => {
    if (!profileData.name.trim()) {
      toast.error('Name cannot be empty')
      return
    }

    try {
      setLoading(true)
      logger.log('profileData =====> ', profileData)
      const formData = new FormData()

      formData.append('name', profileData.name)
      formData.append('address', profileData.address)
      formData.append('city', profileData.city)
      formData.append('state', profileData.state)
      formData.append('postal_code', profileData.postal_code)
      formData.append('country', profileData.country)
      formData.append('gender', profileData.gender)
      formData.append('dob', profileData.dob)

      if (selectedImage) {
        formData.append('profile_image', selectedImage)
      }

      const response = await axiosInstance.put('web-api/customer/update-profile', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })

      if (response.data.status === 200) {
        toast.success('Profile updated successfully')
        if (response?.data?.data) {
          setAuth((prev) => ({
            ...prev,
            user: response.data.data,
          }))
        }
        setIsEditing(false)
      }
    } catch (error) {
      console.error('Error updating profile:', error)
      toast.error(error.response?.data?.message || 'Failed to update profile')
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    setIsEditing(false)
    setSelectedImage(null)
    setProfileData({
      name: user?.name || '',
      email: user?.email || '',
      profile_image: user?.profile_image || '',
      address: user?.address || '',
      city: user?.city || '',
      state: user?.state || '',
      postal_code: user?.postal_code || '',
      country: user?.country || '',
      gender: user?.gender || '',
      dob: user?.dob ? new Date(user.dob).toISOString().split('T')[0] : '',
    })
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-4xl w-full bg-white rounded-2xl shadow-xl p-8 border border-purple-100">
        <h1 className="text-3xl font-bold text-gray-800 text-center mb-8">User Profile</h1>

        <div className="space-y-8">
          {/* Profile Image Section */}
          <div className="flex flex-col items-center gap-4">
            <div className="relative group">
              <img
                src={
                  selectedImage
                    ? profileData.profile_image
                    : profileData.profile_image
                      ? `${import.meta.env.VITE_BASE_URL}${profileData.profile_image}`
                      : '/customer-assets/User.svg'
                }
                alt="Profile"
                className="w-32 h-32 rounded-full object-cover border-4 border-purple-200 shadow-md transition-all duration-200 group-hover:border-purple-400"
              />
              {isEditing && (
                <Label
                  htmlFor="profileImage"
                  className="absolute inset-0 bg-black bg-opacity-60 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                  title="Change profile image"
                >
                  <Edit size={24} className="text-purple-300 group-hover:text-purple-100" />
                  <Input
                    id="profileImage"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    ref={fileInputRef}
                    onChange={handleImageChange}
                  />
                </Label>
              )}
            </div>
            {imageError && <p className="text-sm text-red-600 font-medium">{imageError}</p>}
            <p className="text-lg text-purple-700 font-semibold">{profileData.email}</p>
            {isEditing && (
              <p className="text-sm text-gray-500 mt-1">
                Max size: 5MB. Allowed types: JPG, PNG, GIF.
              </p>
            )}
          </div>

          <hr className="border-purple-100" />

          {/* Form Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="name" className="text-gray-700 font-medium mb-1 block">
                Name
              </Label>
              <Input
                id="name"
                name="name"
                value={profileData.name}
                onChange={handleInputChange}
                disabled={!isEditing}
                className={`w-full p-2 border rounded-md ${
                  isEditing
                    ? 'border-purple-300 focus:ring-2 focus:ring-purple-200'
                    : 'bg-gray-100 border-gray-200 text-gray-600 cursor-not-allowed'
                }`}
                onInput={(e) => {
                  e.target.value = e.target.value.replace(/^\s+/, '')
                }}
              />
            </div>
            <div>
              <Label htmlFor="email" className="text-gray-700 font-medium mb-1 block">
                Email
              </Label>
              <Input
                id="email"
                value={profileData.email}
                disabled
                className="w-full p-2 border rounded-md bg-gray-100 border-gray-200 text-gray-600 cursor-not-allowed"
              />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="address" className="text-gray-700 font-medium mb-1 block">
                Address
              </Label>
              <Textarea
                id="address"
                name="address"
                value={profileData.address}
                onChange={handleInputChange}
                disabled={!isEditing}
                className={`w-full p-2 border rounded-md ${
                  isEditing
                    ? 'border-purple-300 focus:ring-2 focus:ring-purple-200'
                    : 'bg-gray-100 border-gray-200 text-gray-600 cursor-not-allowed'
                }`}
              />
            </div>
            <div>
              <Label htmlFor="city" className="text-gray-700 font-medium mb-1 block">
                City
              </Label>
              <Input
                id="city"
                name="city"
                value={profileData.city}
                onChange={handleInputChange}
                disabled={!isEditing}
                className={`w-full p-2 border rounded-md ${
                  isEditing
                    ? 'border-purple-300 focus:ring-2 focus:ring-purple-200'
                    : 'bg-gray-100 border-gray-200 text-gray-600 cursor-not-allowed'
                }`}
              />
            </div>
            <div>
              <Label htmlFor="state" className="text-gray-700 font-medium mb-1 block">
                State
              </Label>
              <Input
                id="state"
                name="state"
                value={profileData.state}
                onChange={handleInputChange}
                disabled={!isEditing}
                className={`w-full p-2 border rounded-md ${
                  isEditing
                    ? 'border-purple-300 focus:ring-2 focus:ring-purple-200'
                    : 'bg-gray-100 border-gray-200 text-gray-600 cursor-not-allowed'
                }`}
              />
            </div>
            <div>
              <Label htmlFor="postal_code" className="text-gray-700 font-medium mb-1 block">
                Postal Code
              </Label>
              <Input
                id="postal_code"
                type="number"
                name="postal_code"
                value={profileData.postal_code}
                onChange={handleInputChange}
                disabled={!isEditing}
                className={`w-full p-2 border rounded-md ${
                  isEditing
                    ? 'border-purple-300 focus:ring-2 focus:ring-purple-200'
                    : 'bg-gray-100 border-gray-200 text-gray-600 cursor-not-allowed'
                }`}
              />
            </div>
            <div>
              <Label htmlFor="country" className="text-gray-700 font-medium mb-1 block">
                Country
              </Label>
              <Input
                id="country"
                name="country"
                value={profileData.country}
                onChange={handleInputChange}
                disabled={!isEditing}
                className={`w-full p-2 border rounded-md ${
                  isEditing
                    ? 'border-purple-300 focus:ring-2 focus:ring-purple-200'
                    : 'bg-gray-100 border-gray-200 text-gray-600 cursor-not-allowed'
                }`}
              />
            </div>
            <div>
              <Label htmlFor="gender" className="text-gray-700 font-medium mb-1 block">
                Gender
              </Label>
              <select
                id="gender"
                name="gender"
                value={profileData.gender}
                onChange={handleInputChange}
                disabled={!isEditing}
                className={`w-full p-2 border rounded-md appearance-none bg-white pr-8 ${
                  isEditing
                    ? 'border-purple-300 focus:ring-2 focus:ring-purple-200'
                    : 'bg-gray-100 border-gray-200 text-gray-600 cursor-not-allowed'
                }`}
              >
                <option value="">Select</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="relative">
              <Label htmlFor="dob" className="text-gray-700 font-medium mb-1 block">
                Date of Birth
              </Label>
              <Input
                id="dob"
                type="date"
                name="dob"
                value={profileData.dob}
                onChange={handleInputChange}
                disabled={!isEditing}
                className={`w-full p-2 border rounded-md ${
                  isEditing
                    ? 'border-purple-300 focus:ring-2 focus:ring-purple-200'
                    : 'bg-gray-100 border-gray-200 text-gray-600 cursor-not-allowed'
                }`}
              />
            </div>
          </div>

          {/* Buttons */}
          <div className="flex flex-col-reverse md:flex-row  md:justify-between gap-3 md:gap-0 md:items-center pt-6 border-t border-purple-100">
            <Button
              variant="outline"
              onClick={() => window.history.back()}
              className="px-6 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100 transition-colors duration-200"
            >
              Back
            </Button>
            {isEditing ? (
              <div className=" flex flex-col-reverse sm:flex-row gap-3 sm:justify-between ">
                <Button
                  variant="outline"
                  onClick={handleCancel}
                  className="px-6 py-2 rounded-md border border-red-300 text-red-600 hover:bg-red-50 transition-colors duration-200"
                >
                  Cancel
                </Button>
                <Button
                  className="px-6 py-2 rounded-md bg-purple-600 hover:bg-purple-700 text-white shadow-md hover:shadow-lg transition-all duration-200"
                  onClick={handleSave}
                  disabled={loading}
                >
                  {loading ? (
                    <div className="flex items-center gap-2">
                      <div className="border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Saving...
                    </div>
                  ) : (
                    'Save Changes'
                  )}
                </Button>
              </div>
            ) : (
              <Button
                className="px-6 py-2 rounded-md bg-purple-600 hover:bg-purple-700 text-white shadow-md hover:shadow-lg transition-all duration-200"
                onClick={() => setIsEditing(true)}
              >
                Edit Profile
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default UpdateUserProfile
